import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { OnboardingStyle } from '../../../public/onboarding/onboarding-style';
import usePushDataLayerEvent from '../../../../utils/google-analytics.util';
import { hideModal, showModal } from '../../../../store/slices/generalSlice';
import { RootState } from '../../../../store/rootReducer';
import storageService from '../../../../services/storage.service';
import { AssistanceInformation } from '../../../../models/assistance.model';
import useManageStorage from '../../../../hooks/useManageStorage/useManageStorage';
import useAssistance from '../../../../hooks/useAssistance/useAssistance';
import { AuthHeader, FormLayout } from '../../../../global-styles/layout';
import { Heading2Text, Subhead02Text } from '../../../../components/typography/typography';
import Navbar from '../../../../components/navbar/navbar';
import ModalContainer from '../../../../components/common/modal-container/modal-container';
import AlxButton from '../../../../components/alx-components/button';
import { ISuccessProps } from './success-types';
import { SuccessStyle } from './success-style';

const Success: React.FC<ISuccessProps> = ({
  name,
  surname,
  assistanceInfo,
  nextStep,
  onCheckResume,
  resetForms,
  isDelayed,
}) => {
  const { t } = useTranslation(['common', 'auth']);
  const queryClient = useQueryClient();
  const { cancelAssistance } = useAssistance({});
  const dispatch = useDispatch();
  const modal = useSelector((state: RootState) => state.general.modal);
  const navigate = useNavigate();
  const { deleteFromStorage } = useManageStorage();
  const { pushDataLayerEvent } = usePushDataLayerEvent();

  const assistanceData = queryClient.getQueryData<AssistanceInformation>(['assitanceInformation']);

  const handleCancelAssistance = useCallback(() => {
    dispatch(showModal({ name: 'cancel-assitance' }));
    pushDataLayerEvent(
      'click_varios',
      {
        tipo_de_click: 'link',
        boton_click: '10a anular servicio',
      },
      'grua-en-camino',
    );
  }, [dispatch, pushDataLayerEvent]);

  const handleTextInfo = useCallback(() => {
    if (!assistanceData) return;
    const { file } = assistanceData;

    return file ? <Trans t={t} i18nKey="auth:unregistered.success.text-info" values={{ file }} /> : null;
  }, [assistanceData, t]);

  let right = {};
  if (assistanceInfo) {
    right = {
      clickRight: () => {
        handleCancelAssistance();
      },
      customRight: (
        <div data-cy="onboarding-nav" className="onboarding-nav">
          <p>{t('auth:unregistered.success.cancel-service')}</p>
        </div>
      ),
    };
  }
  return (
    <>
      <SuccessStyle>
        <OnboardingStyle>
          <Navbar assistance {...right} />
          <div className="onboarding-top">
            <AuthHeader>
              <div className="auth-header-title">
                <Heading2Text as="h1" weight={600}>
                  {isDelayed
                    ? t('auth:unregistered.success.title-delayed', { name: `${name} ${surname}` })
                    : t('auth:unregistered.success.title', { name: `${name} ${surname}` })}
                </Heading2Text>
              </div>
            </AuthHeader>
            <div className="success-text m-24px">
              <Subhead02Text>{handleTextInfo()}</Subhead02Text>
            </div>
            <div className="success-text m-24px">
              <Subhead02Text>{t('auth:unregistered.success.text-info-2')}</Subhead02Text>
            </div>
          </div>
          <FormLayout>
            <AlxButton onClick={nextStep} data-cy="next-step-btn">
              {onCheckResume ? t('common:buttons.tracking') : t('common:buttons.next')}
            </AlxButton>
            {onCheckResume && (
              <AlxButton
                className="ghost"
                onClick={() => {
                  pushDataLayerEvent('click_varios', {
                    tipo_de_click: 'link',
                    boton_click: '10e resumen',
                  });
                  onCheckResume();
                }}
                data-cy="login-submit"
              >
                {t('common:buttons.check-resume')}
              </AlxButton>
            )}
          </FormLayout>
        </OnboardingStyle>
      </SuccessStyle>
      {/* MODALS */}
      <ModalContainer
        buttons={[
          {
            className: 'primary',
            dataCy: 'accept-cancel-btn',
            onClick: () => {
              dispatch(hideModal());
              cancelAssistance.mutate(assistanceInfo, {
                onSuccess: async () => {
                  await storageService.removeItem('metaData');
                  deleteFromStorage(['interventionData', 'activeAssistance', 'assitanceInformation']);
                  navigate('/cancelled');
                },
                onError: () => {
                  // TODO: handle errors in a better way (toast)
                  console.error('something went wrong');
                },
              });
            },
            label: t('auth:unregistered.success.cancel-assistance-modal.cancel-assistance-button'),
          },
          {
            className: 'secondary',
            onClick: () => dispatch(hideModal()),
            label: t('auth:unregistered.success.cancel-assistance-modal.cancel-button'),
          },
        ]}
        active={modal.name === 'cancel-assitance'}
        onClose={() => {
          dispatch(hideModal());
        }}
        title={t('auth:unregistered.success.cancel-assistance-modal.title')}
        text={t('auth:unregistered.success.cancel-assistance-modal.description')}
        dataCy="reset-form-modal"
      />

      <ModalContainer
        buttons={[
          {
            dataCy: 'ok-btn',
            className: 'primary',
            onClick: () => {
              resetForms(['activeAssistance', 'assitanceInformation']);
              navigate('/');
            },
            label: t('common:buttons.accept'),
          },
        ]}
        active={modal.name === 'assitance-cancel-successful'}
        onClose={() => {
          dispatch(hideModal());
        }}
        title={t('auth:unregistered.success.confirmed-assistance-cancelation.title')}
        text={t('auth:unregistered.success.confirmed-assistance-cancelation.description')}
        dataCy="reset-form-modal"
      />
    </>
  );
};

export default Success;
